import { useState, useEffect, useCallback } from 'react';

// ── App-level script clipboard ──────────────────────────────────────────────
// A persistent clipboard slot for cross-script (and within-script) copy/paste in
// the script editor.
//
// Stored in localStorage, NOT sessionStorage: the whole point is to copy in one
// script and paste in another, and scripts are commonly opened in SEPARATE TABS.
// sessionStorage is per-tab and is NOT shared between separate tabs, so a copy in
// one tab never reaches another — which is exactly the cross-script case. local-
// Storage is shared across all tabs of the same origin, and its `storage` event
// notifies the other tabs live (so the Paste badge updates everywhere).
//
// localStorage persists until cleared (it survives a browser restart), so to
// avoid pasting something copied days ago we treat a payload older than MAX_AGE
// as empty. The single slot is overwritten on each copy, and the toast's "Clear"
// action removes it on demand.
//
// One slot only. The companion system-clipboard TSV write (for Google Sheets /
// Excel) is handled by the caller; this hook owns only the app-level slot.
//
// Two payload shapes share these common fields:
//   { sourceScriptId, sourceScriptName, sourceFormat, copiedAt }   // copiedAt = epoch ms
// plus one of:
//   kind:'cells'  → a rectangular cell block, pasted as a spreadsheet OVERLAY:
//                   { colKeys: [<srcKey>,…],              // grid columns, left→right
//                     grid: [ [ {v, color?, link?}, … ], … ] }  // rows top→bottom
//   kind:'rows'   → whole rows (gutter-selected), pasted as INSERTED rows:
//                   { rows: [ { <colKey>: value, …, _rowColor?, _cellColors?, _links? }, … ] }

export const SCRIPT_CLIPBOARD_KEY = 'hf_script_clipboard';
const MAX_AGE_MS = 24 * 60 * 60 * 1000; // ignore clipboard older than 24h

function readClipboard() {
  try {
    const raw = localStorage.getItem(SCRIPT_CLIPBOARD_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed) return null;
    // Soft-expire stale clipboards so we don't paste something from a past session.
    if (parsed.copiedAt && Date.now() - parsed.copiedAt > MAX_AGE_MS) return null;
    // Two payload shapes: a cell grid (kind:'cells') or whole rows (kind:'rows').
    const hasGrid = Array.isArray(parsed.grid);
    const hasRows = Array.isArray(parsed.rows);
    if (!hasGrid && !hasRows) return null;
    return parsed;
  } catch {
    return null;
  }
}

// Row count for the toolbar badge — the number of rows the copy spans, for both
// a cell grid (grid height) and a whole-row copy (rows length).
function clipRowCount(payload) {
  if (!payload) return 0;
  if (Array.isArray(payload.grid)) return payload.grid.length;
  if (Array.isArray(payload.rows)) return payload.rows.length;
  return 0;
}

export function useScriptClipboard() {
  const [payload, setPayload] = useState(readClipboard);

  // Cross-tab + same-tab reactivity. The `storage` event fires in OTHER tabs when
  // localStorage changes, so a copy in one tab updates this tab's badge/state.
  // writeClipboard/clearClipboard also setPayload directly for the originating tab
  // (the storage event does not fire in the tab that made the change). `focus`
  // re-reads when returning to a tab that may have been updated while hidden.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === SCRIPT_CLIPBOARD_KEY || e.key === null) setPayload(readClipboard());
    };
    const onFocus = () => setPayload(readClipboard());
    window.addEventListener('storage', onStorage);
    window.addEventListener('focus', onFocus);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  const writeClipboard = useCallback((next) => {
    try {
      localStorage.setItem(SCRIPT_CLIPBOARD_KEY, JSON.stringify(next));
    } catch {
      /* localStorage may be full or unavailable — keep in-memory state anyway */
    }
    setPayload(next);
  }, []);

  const clearClipboard = useCallback(() => {
    try {
      localStorage.removeItem(SCRIPT_CLIPBOARD_KEY);
    } catch {
      /* ignore */
    }
    setPayload(null);
  }, []);

  const rowCount = clipRowCount(payload);

  return { payload, rowCount, writeClipboard, clearClipboard };
}
